import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Layout from './components/layout/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import TimesheetEntry from './pages/TimesheetEntry'
import TimesheetList from './pages/TimesheetList'
import Users from './pages/admin/Users'
import UserEdit from './pages/admin/UserEdit'
import Inventory from './pages/Inventory'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import CreateTimesheet from './pages/CreateTimesheet'
import EmailPackingSlip from './pages/EmailPackingSlip'
import SignatureCopy from './pages/admin/SignatureCopy'
import ManageTimesheets from './pages/ManageTimesheets'
import SyncDashboard from './pages/SyncDashboard'
import JobList from './pages/JobList'
import TimesheetRollback from './pages/TimesheetRollback'
import SketchPad from './pages/SketchPad'
import SketchList from './pages/SketchList'
import CompanySettings from './pages/CompanySettings'

function ProtectedRoute({ children, permission = null }) {
    const { user, loading, isSysAdmin } = useAuth()

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    if (!user) {
        return <Navigate to="/login" replace />
    }

    // SysAdmin always has access
    if (isSysAdmin) {
        return children
    }

    // Check specific permission
    if (permission && !user[permission]) {
        return <Navigate to="/" replace />
    }

    return children
}

function AppRoutes() {
    const isOnline = useOnlineStatus()

    return (
        <>
            {!isOnline && (
                <div className="offline-indicator">
                    You are offline. Changes will sync when connected.
                </div>
            )}
            <Routes>
                <Route path="/login" element={<Login />} />

                <Route path="/" element={
                    <ProtectedRoute>
                        <Layout>
                            <Dashboard />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/create-timesheet" element={
                    <ProtectedRoute>
                        <Layout>
                            <CreateTimesheet />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/timesheet/new" element={
                    <ProtectedRoute>
                        <Layout>
                            <TimesheetEntry />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/timesheet/:uuid" element={
                    <ProtectedRoute>
                        <Layout>
                            <TimesheetEntry />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/timesheets" element={
                    <ProtectedRoute>
                        <Layout>
                            <TimesheetList />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/manage-timesheets" element={
                    <ProtectedRoute permission="canManageTimesheets">
                        <Layout>
                            <ManageTimesheets />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/admin/email-packing-slip" element={
                    <ProtectedRoute permission="canPackingSlipEmail">
                        <Layout>
                            <EmailPackingSlip />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/inventory" element={
                    <ProtectedRoute permission="canInventory">
                        <Layout>
                            <Inventory />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/admin/users" element={
                    <ProtectedRoute permission="canUsers">
                        <Layout>
                            <Users />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/admin/users/:id" element={
                    <ProtectedRoute permission="canUsers">
                        <Layout>
                            <UserEdit />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/admin/signature-copy" element={
                    <ProtectedRoute permission="canSigCopy">
                        <Layout>
                            <SignatureCopy />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/admin/sync" element={
                    <ProtectedRoute>
                        <Layout>
                            <SyncDashboard />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/admin/rollback" element={
                    <ProtectedRoute>
                        <Layout>
                            <TimesheetRollback />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/job-list" element={
                    <ProtectedRoute>
                        <Layout>
                            <JobList />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/timesheet/:uuid/sketches" element={
                    <ProtectedRoute>
                        <Layout>
                            <SketchList />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/timesheet/:uuid/sketch" element={
                    <ProtectedRoute>
                        <SketchPad />
                    </ProtectedRoute>
                } />

                <Route path="/company-settings" element={
                    <ProtectedRoute>
                        <CompanySettings />
                    </ProtectedRoute>
                } />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    )
}

export default function App() {
    return (
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    )
}